import {hpcAttrs} from '@github-ui/hpc'
import {
  IpmErrorBoundary,
  reportIpmAction,
  reportIpmDismiss,
  reportIpmVisible,
  type IpmMetricTags,
  useIpmRenderTiming,
} from '@github-ui/ipm-telemetry'
import {verifiedFetch} from '@github-ui/verified-fetch'
import {XIcon} from '@primer/octicons-react'
import {IconButton} from '@primer/react'
import {Profiler, useEffect, useMemo, useRef, useState} from 'react'
import styles from './PullRequestSidebarNudge.module.css'

export interface PullRequestSidebarNudgeAnalytics {
  category: string
  location: string
  ctaAction: string
  organizationId?: number | string
  plan?: string
}

export interface PullRequestSidebarNudgeProps {
  title: string
  description: string
  imagePathLight: string
  imagePathDark: string
  imageAltLight?: string
  imageAltDark?: string
  ctaTitle: string
  ctaHref: string
  dismissPath: string
  analytics: PullRequestSidebarNudgeAnalytics
  testSelector: string
  useDismissAndRedirect?: boolean
}

export function PullRequestSidebarNudge({
  title,
  description,
  imagePathLight,
  imagePathDark,
  imageAltLight = '',
  imageAltDark = '',
  ctaTitle,
  ctaHref,
  dismissPath,
  analytics,
  testSelector,
  useDismissAndRedirect = false,
}: PullRequestSidebarNudgeProps) {
  const [hidden, setHidden] = useState(false)
  const dismissAnnouncementRef = useRef<HTMLSpanElement>(null)
  const telemetryTags = useMemo<IpmMetricTags>(
    () => ({
      package: 'in-product-targeting',
      component: 'PullRequestSidebarNudge',
      surface: 'sidebar_nudge',
      display_location: analytics.location,
      category: analytics.category,
      nudge_id: testSelector,
      variant: useDismissAndRedirect ? 'dismiss_and_redirect' : 'default',
    }),
    [analytics.category, analytics.location, testSelector, useDismissAndRedirect],
  )
  const renderTiming = useIpmRenderTiming(telemetryTags)

  useEffect(() => {
    reportIpmVisible(telemetryTags)
  }, [telemetryTags])

  const handleDismiss = async () => {
    reportIpmDismiss(telemetryTags)
    try {
      await verifiedFetch(dismissPath, {method: 'POST'})
    } catch {
      reportIpmAction('error', telemetryTags, {samplingProbability: 1})
      // Hide the card even if the server didn't record the dismiss.
    } finally {
      setHidden(true)
    }
  }

  const handleCtaClick = () => {
    reportIpmAction('cta_click', telemetryTags)
    if (!useDismissAndRedirect) return
    const dismissAndForget = async () => {
      try {
        await verifiedFetch(dismissPath, {method: 'POST', keepalive: true})
      } catch {
        reportIpmAction('error', telemetryTags, {samplingProbability: 1})
        // Browser still follows the CTA href.
      }
    }
    void dismissAndForget()
  }

  // Move focus to the live region so dismissal isn't silent for AT/keyboard
  // users and focus doesn't collapse to <body> (WCAG 2.4.3).
  useEffect(() => {
    if (hidden) {
      dismissAnnouncementRef.current?.focus()
    }
  }, [hidden])

  if (hidden) {
    return (
      <span
        ref={dismissAnnouncementRef}
        tabIndex={-1}
        role="status"
        aria-live="polite"
        className={styles.dismissAnnouncement}
      >
        Nudge dismissed.
      </span>
    )
  }

  const analyticsLabel = `location:${analytics.location}`

  const nudge = (
    <>
      <span data-test-selector={testSelector} />
      <div
        className={styles.container}
        {...hpcAttrs()}
        data-testid="pull-request-sidebar-nudge"
        data-analytics-visible={JSON.stringify({
          category: analytics.category,
          action: 'visible',
          label: analyticsLabel,
          ...(analytics.organizationId !== undefined && {organizationId: analytics.organizationId}),
          ...(analytics.plan !== undefined && {plan: analytics.plan}),
        })}
      >
        <div className={styles.dismiss}>
          <IconButton
            icon={XIcon}
            variant="invisible"
            aria-label="Dismiss"
            onClick={handleDismiss}
            data-analytics-event={JSON.stringify({
              category: analytics.category,
              action: 'dismiss',
              label: analyticsLabel,
            })}
          />
        </div>

        <div className={styles.imageContainer}>
          <img src={imagePathLight} alt={imageAltLight} className={`${styles.image} ${styles.imageLight}`} />
          <img src={imagePathDark} alt={imageAltDark} className={`${styles.image} ${styles.imageDark}`} />
        </div>

        <div className={styles.main}>
          <h3 className={styles.title}>{title}</h3>
          <div className={styles.description}>{description}</div>

          <a
            href={ctaHref}
            onClick={handleCtaClick}
            className={`${styles.cta} btn btn-sm btn-primary text-small px-2`}
            data-test-selector={`${testSelector}-cta`}
            data-analytics-event={JSON.stringify({
              category: analytics.category,
              action: analytics.ctaAction,
              label: analyticsLabel,
            })}
          >
            {ctaTitle}
          </a>
        </div>
      </div>
    </>
  )

  const content = !renderTiming.isEnabled ? (
    nudge
  ) : (
    <Profiler id="PullRequestSidebarNudge" onRender={renderTiming.onRender}>
      {nudge}
    </Profiler>
  )

  return (
    <IpmErrorBoundary tags={telemetryTags} boundaryName="PullRequestSidebarNudge">
      {content}
    </IpmErrorBoundary>
  )
}
